import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, 
  Mail, 
  Inbox,
  Loader2,
  FolderTree,
  FileText,
  Package,
  Plus,
  Trash2,
  Settings,
  CheckCircle,
  XCircle,
  RefreshCw,
  Server,
  Link,
  Folder
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MailImport = () => {
  const { token, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('upload');
  
  // File upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [folders, setFolders] = useState([]);
  
  // Link during import
  const [linkOnImport, setLinkOnImport] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Mailbox connections
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionForm, setConnectionForm] = useState({
    name: '',
    provider_type: 'imap',
    host: '',
    port: 993,
    username: '',
    password: '',
    use_ssl: true,
    folder_to_import: 'INBOX',
    auto_import: false
  });
  const [savingConnection, setSavingConnection] = useState(false);
  const [testingConnection, setTestingConnection] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/correspondence-folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data.folders);
    } catch (error) {
      console.error('Failed to load folders');
    }
  }, [token]);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mailbox-connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnections(response.data.connections);
    } catch (error) {
      console.error('Failed to load connections');
    } finally {
      setLoadingConnections(false);
    }
  }, [token]);

  const fetchEntities = useCallback(async () => {
    try {
      const [docsRes, assetsRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDocuments(docsRes.data.documents || []);
      setAssets(assetsRes.data.assets || []);
      setProjects(projectsRes.data.projects || []);
    } catch (error) {
      console.error('Failed to load entities');
    }
  }, [token]);

  useEffect(() => {
    fetchFolders();
    fetchConnections();
    fetchEntities();
  }, [fetchFolders, fetchConnections, fetchEntities]);

  // File upload handler
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (selectedFolderId) formData.append('folder_id', selectedFolderId);
    
    try {
      const response = await axios.post(`${API_URL}/api/correspondence/import-file`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Email imported: ${response.data.subject}`);
      
      // If linking enabled, update the correspondence with links
      if (linkOnImport && (selectedDocuments.length || selectedAssets.length || selectedProjects.length)) {
        await axios.put(`${API_URL}/api/correspondence/${response.data.id}`, {
          document_ids: selectedDocuments,
          asset_ids: selectedAssets,
          project_ids: selectedProjects
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Links added to correspondence');
      }
      
      // Reset form
      setUploadFile(null);
      setSelectedDocuments([]);
      setSelectedAssets([]);
      setSelectedProjects([]);
      setLinkOnImport(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import email');
    } finally {
      setUploading(false);
    }
  };

  // Connection handlers
  const handleCreateConnection = async (e) => {
    e.preventDefault();
    setSavingConnection(true);
    
    try {
      await axios.post(`${API_URL}/api/mailbox-connections`, connectionForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mailbox connection created');
      setShowConnectionModal(false);
      setConnectionForm({
        name: '',
        provider_type: 'imap',
        host: '',
        port: 993,
        username: '',
        password: '',
        use_ssl: true,
        folder_to_import: 'INBOX',
        auto_import: false
      });
      fetchConnections();
    } catch (error) {
      toast.error('Failed to create connection');
    } finally {
      setSavingConnection(false);
    }
  };

  const handleTestConnection = async (connId) => {
    setTestingConnection(connId);
    try {
      const response = await axios.post(`${API_URL}/api/mailbox-connections/${connId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Connection test successful');
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteConnection = async (connId) => {
    if (!window.confirm('Delete this mailbox connection?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/mailbox-connections/${connId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Connection deleted');
      fetchConnections();
    } catch (error) {
      toast.error('Failed to delete connection');
    }
  };

  const toggleEntitySelection = (type, id, setter) => {
    setter(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Check permission
  if (!hasPermission('admin') && !hasPermission('manage_correspondence')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <XCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Project Mailbox</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Import emails and manage mailbox connections
          </p>
        </div>
      </div>

      <div className="p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload File
            </TabsTrigger>
            <TabsTrigger value="connections" className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              Mailbox Connections
            </TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Import Email File
                  </CardTitle>
                  <CardDescription>
                    Upload .eml or .msg files to import emails
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleFileUpload} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Email File</Label>
                      <div className="border-2 border-dashed rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".eml,.msg"
                          onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="email-file"
                        />
                        <label htmlFor="email-file" className="cursor-pointer">
                          <Mail className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                          {uploadFile ? (
                            <p className="text-sm font-medium">{uploadFile.name}</p>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Click to select .eml or .msg file
                            </p>
                          )}
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Destination Folder</Label>
                      <Select value={selectedFolderId || "none"} onValueChange={(v) => setSelectedFolderId(v === "none" ? "" : v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select folder (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No folder</SelectItem>
                          {folders.map(folder => (
                            <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={linkOnImport}
                        onCheckedChange={setLinkOnImport}
                      />
                      <Label>Link to entities during import</Label>
                    </div>

                    <Button type="submit" className="w-full" disabled={!uploadFile || uploading}>
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Import Email
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Link during import panel */}
              {linkOnImport && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Link className="w-5 h-5" />
                      Link to Entities
                    </CardTitle>
                    <CardDescription>
                      Select entities to link with the imported email
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Documents */}
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground mb-2 block">Documents</Label>
                      <div className="border rounded-lg p-2 max-h-32 overflow-y-auto">
                        {documents.slice(0, 10).map(doc => (
                          <div 
                            key={doc.id}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                              selectedDocuments.includes(doc.id) ? 'bg-primary/10' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => toggleEntitySelection('documents', doc.id, setSelectedDocuments)}
                          >
                            <Checkbox checked={selectedDocuments.includes(doc.id)} onChange={() => {}} />
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm truncate">{doc.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Assets */}
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground mb-2 block">Assets</Label>
                      <div className="border rounded-lg p-2 max-h-32 overflow-y-auto">
                        {assets.slice(0, 10).map(asset => (
                          <div 
                            key={asset.id}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                              selectedAssets.includes(asset.id) ? 'bg-primary/10' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => toggleEntitySelection('assets', asset.id, setSelectedAssets)}
                          >
                            <Checkbox checked={selectedAssets.includes(asset.id)} onChange={() => {}} />
                            <Package className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm truncate">{asset.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Projects */}
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground mb-2 block">Projects</Label>
                      <div className="border rounded-lg p-2 max-h-32 overflow-y-auto">
                        {projects.slice(0, 10).map(project => (
                          <div 
                            key={project.id}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                              selectedProjects.includes(project.id) ? 'bg-primary/10' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => toggleEntitySelection('projects', project.id, setSelectedProjects)}
                          >
                            <Checkbox checked={selectedProjects.includes(project.id)} onChange={() => {}} />
                            <FolderTree className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm truncate">{project.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Connections Tab */}
          <TabsContent value="connections">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Mailbox Connections</CardTitle>
                  <CardDescription>Configure email server connections for importing</CardDescription>
                </div>
                <Button onClick={() => setShowConnectionModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Connection
                </Button>
              </CardHeader>
              <CardContent>
                {loadingConnections ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : connections.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Server className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No mailbox connections configured</p>
                    <p className="text-sm">Add a connection to import emails from a mailbox</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {connections.map(conn => (
                      <div key={conn.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-lg bg-muted">
                            <Inbox className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-medium">{conn.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {conn.provider_type.toUpperCase()} • {conn.username}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={conn.is_active ? 'default' : 'secondary'}>
                                {conn.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {conn.auto_import && (
                                <Badge variant="outline">Auto-import</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleTestConnection(conn.id)}
                            disabled={testingConnection === conn.id}
                          >
                            {testingConnection === conn.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteConnection(conn.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Connection Modal */}
      <Dialog open={showConnectionModal} onOpenChange={setShowConnectionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Mailbox Connection</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateConnection} className="space-y-4">
            <div className="space-y-2">
              <Label>Connection Name</Label>
              <Input
                value={connectionForm.name}
                onChange={(e) => setConnectionForm({ ...connectionForm, name: e.target.value })}
                placeholder="My Work Email"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Provider Type</Label>
              <Select 
                value={connectionForm.provider_type} 
                onValueChange={(v) => setConnectionForm({ ...connectionForm, provider_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="imap">IMAP</SelectItem>
                  <SelectItem value="exchange">Microsoft Exchange</SelectItem>
                  <SelectItem value="gmail">Gmail</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Host</Label>
                <Input
                  value={connectionForm.host}
                  onChange={(e) => setConnectionForm({ ...connectionForm, host: e.target.value })}
                  placeholder="imap.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={connectionForm.port}
                  onChange={(e) => setConnectionForm({ ...connectionForm, port: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Username/Email</Label>
              <Input
                value={connectionForm.username}
                onChange={(e) => setConnectionForm({ ...connectionForm, username: e.target.value })}
                placeholder="user@example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={connectionForm.password}
                onChange={(e) => setConnectionForm({ ...connectionForm, password: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Folder to Import</Label>
              <Input
                value={connectionForm.folder_to_import}
                onChange={(e) => setConnectionForm({ ...connectionForm, folder_to_import: e.target.value })}
                placeholder="INBOX"
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={connectionForm.use_ssl}
                  onCheckedChange={(checked) => setConnectionForm({ ...connectionForm, use_ssl: checked })}
                />
                <Label>Use SSL</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={connectionForm.auto_import}
                  onCheckedChange={(checked) => setConnectionForm({ ...connectionForm, auto_import: checked })}
                />
                <Label>Auto Import</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowConnectionModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingConnection}>
                {savingConnection ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Create Connection
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MailImport;
